import { type FormFieldsVisibilityState } from "@/lib/form-field-visibility";
import { isFieldVisibleInFiche } from "@/config/field-visibility/always-visible-fields";
import type { ArtworkPrintSettings } from "./artworkPrintSettings";

/**
 * Print settings that map 1:1 (validated) to a maskable field of the artwork
 * fiche. ONLY these are driven by the visibility grid when the "force displayed
 * fields" option is on; every other print setting keeps the user's manual
 * choice (financial tracking, relations, dimensions, formatting flags… have no
 * maskable field to read from).
 */
export const ARTWORK_PRINT_SETTINGS_VISIBILITY_MAP: Partial<
  Record<keyof ArtworkPrintSettings, string>
> = {
  // -- Infos de base --
  showArtist: "relatedAuthorIds",
  showTitle: "oeuvresDescriptionTitre",
  showCreationDate: "oeuvresDescriptionDateDeCreation",
  showRegistrationNumber: "oeuvresDescriptionNumeroEnregistrement",
  showTechnique: "oeuvresDescriptionTechnique",
  showDimensions: "oeuvresDescriptionDimensionsOeuvre",
  showSignature: "oeuvresDescriptionSignatureLocalisation",
  showInscriptionsBack: "oeuvresDescriptionInscriptionsDosMultilingual",
  // -- Description --
  showCreationPlace: "oeuvresDescriptionLieuDeCreation",
  showInventoryNumber: "oeuvresDescriptionNumeroDInventaire",
  showCollectionRef: "oeuvresDescriptionReferenceCollectionActuelle",
  showDoubleFace: "oeuvresDescriptionOeuvreDoubleFace",
  oeuvresDescriptionSousTitre: "oeuvresDescriptionSousTitre",
  oeuvresDescriptionAutreTitre: "oeuvresDescriptionAutreTitre",
  showSupport: "oeuvresDescriptionSupport",
  showMovement: "oeuvresDescriptionEcoleMouvement",
  showGeographicArea: "oeuvresDescriptionAireGeographique",
  showPeriod: "oeuvresDescriptionPeriodeCreation",
  // -- Sans section --
  oeuvresDescriptionReference: "oeuvresDescriptionReference",
  showCategory: "oeuvresDescriptionCategorie",
  showTheme: "oeuvresDescriptionTheme",
  showSeries: "oeuvresDescriptionSerie",
  oeuvresValidationNotesDeTravail: "oeuvresValidationNotesDeTravail",
  oeuvresValidationRemarquesPubliques:
    "oeuvresValidationRemarquesPubliquesMultilingual",
  showPersonalNotes: "oeuvresValidationRemarquesPersonnelles",
  // -- Dimensions & poids --
  showCustomDimensions: "oeuvresDescriptionDimensionsOeuvreRaw",
  showWeight: "oeuvresDescriptionPoidsOeuvre",
  showFrameDimensions: "oeuvresRegieDimensionsCadre",
  // -- Localisation / historique --
  showCurrentCollection: "oeuvresLocalisationHistoriqueCollectionId",
  showNotLocated: "oeuvresLocalisationHistoriqueOeuvreNonLocalisee",
  oeuvresLocalisationHistorique: "oeuvresLocalisationHistoriqueNotes",
  // -- Rangement --
  showStorageLocation: "oeuvresLocalisationHistoriqueLieuDeStockage",
  showStorageRoom: "oeuvresRangementRecolementPieceBox",
  showStorageRow: "oeuvresRangementRecolementRangee",
  showStorageRack: "oeuvresRangementRecolementRack",
  showStorageFurniture: "oeuvresRangementRecolementMeuble",
  showStorageShelf: "oeuvresRangementRecolementEtagere",
  showStorageBox: "oeuvresRangementRecolementBoiteCarton",
  showStorageBinder: "oeuvresRangementRecolementClasseur",
  showStorageOtherRef: "oeuvresRangementRecolementAutreReferenceRangement",
  showStorageDate: "oeuvresRangementRecolementDateRangement",
  // -- Autres --
  // `oeuvresDescriptionEnsemble` (nom du CR) n'est plus lié à un champ de la
  // fiche : il s'imprime que l'œuvre fasse partie du CR ou non (#1093).
  showCreditsArtwork: "oeuvresCreditsCreditOeuvre",
  showCreditsPhoto: "oeuvresCreditsCreditPhotoOeuvre",
  oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs:
    "oeuvresAdministratifNotes",
  oeuvresRegieNotesCondition: "oeuvresRegieNotesCondition",
  showRestored: "oeuvresRestored",
  showToRestore: "oeuvresToRestore",
  // -- Suivi financier : aucun mapping. Le masquage de champs est désactivé
  //    sur tout l'onglet (`disableFieldMasking` dans edit-artwork.tsx), donc
  //    ses champs sont toujours affichés dans la fiche : les réglages
  //    d'impression correspondants gardent le choix manuel de l'utilisateur.
  // -- Description (extended) --
  showOtherNumber: "oeuvresDescriptionNumeroAutre",
  showFormerNumber: "oeuvresInformationAncienNumero",
  showRating: "oeuvresValidationSystemeDeNotationDeLoeuvre",
  showStatus: "oeuvresStatus",
  showSummary: "oeuvresDescriptionResume",
  showKeywords: "oeuvresDescriptionAjoutDeMotsCles",
  showFormerTitle: "oeuvresDescriptionAncienTitre",
  showArtworkType: "oeuvresDescriptionTypeOeuvreMultilingual",
  showSupportRemark: "oeuvresDescriptionRemarqueSupport",
  showManufacturing: "oeuvresDescriptionRealisationFabrication",
  showLongDescription: "oeuvresDescriptionDescriptionLongue",
  showDigitalFileName: "oeuvresDescriptionNomFichierNumerique",
  // -- Localisation / historique (extended) --
  showCollectionCatalogueName: "oeuvresLocalisationHistoriqueCollectionCatalogue",
  showCollectionNote: "oeuvresLocalisationHistoriqueCollectionNote",
  showDepositDate: "oeuvresLocalisationHistoriqueDateDepotAffectionPret",
  showDepositReturnDate: "oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret",
  showDepositNote: "oeuvresLocalisationHistoriqueNoteDepotAffectionPret",
  // -- Régie (cadre / caisse) --
  showFrameDescription: "oeuvresRegieDescriptionCadre",
  showRegieNotes: "oeuvresRegieNotesRegie",
  showCrateDescription: "oeuvresRegieDescriptionCaisse",
  showCrateDimensions: "oeuvresRegieDimensionsCaisse",
  showCrateWeight: "oeuvresRegiePoidsCaisse",
  showCrateWeightWithArtwork: "oeuvresRegiePoidsCaisseAvecOeuvre",
  // -- Rangement / récolement (extended) --
  showStorageNotes: "oeuvresLocalisationHistoriqueNotesRangement",
  showMarkingDate: "oeuvresRangementRecolementDateMarquage",
  showRecolementOperator: "oeuvresRangementRecolementOperateurRecolement",
  showRecolementDate: "oeuvresRangementRecolementDateRecolement",
  showRecolementCampaign: "oeuvresRangementRecolementReferenceCampagneRecolement",
  showRecolementOtherRef: "oeuvresRangementRecolementAutreReferenceRecolement",
  showRecolementNotes: "oeuvresRangementRecolementNotesRecolement",
  // -- Crédits / droits (extended) + sources --
  showRightsNature: "oeuvresCreditsNatureDroits",
  showRightsAmount: "oeuvresCreditsMontantDroits",
  showRightsRemark: "oeuvresCreditsRemarqueCreditsDroits",
  showSources: "sources",
};

/**
 * Onglet de fiche (`AF.Tab value` dans edit-artwork.tsx) auquel appartient
 * chaque réglage d'impression de CONTENU. Sert à exclure de l'impression les
 * champs des onglets masqués (visibilité par onglet). Les champs de l'onglet
 * non masquable "generalInformation" (artiste, titre, n°, date, technique,
 * dimensions, signature, inscriptions, ensemble, notes de travail…) et les
 * options de FORMAT (hideX) sont volontairement omis : toujours conservés.
 */
export const PRINT_SETTING_TO_ARTWORK_TAB: Partial<
  Record<keyof ArtworkPrintSettings, string>
> = {
  // -- Suivi financier (onglet "suivi-financier") --
  showEstimationAmount: "suivi-financier",
  showEstimationDescription: "suivi-financier",
  showEstimationDate: "suivi-financier",
  showInsuranceValue: "suivi-financier",
  showInsuranceDate: "suivi-financier",
  showInsuranceNotes: "suivi-financier",
  showSaleDescription: "suivi-financier",
  showSaleDate: "suivi-financier",
  showSaleAmount: "suivi-financier",
  showSaleVAT: "suivi-financier",
  showSaleFees: "suivi-financier",
  showAcquisitionDescription: "suivi-financier",
  showAcquisitionDate: "suivi-financier",
  showAcquisitionAmount: "suivi-financier",
  showAcquisitionVAT: "suivi-financier",
  showAcquisitionCustomsDuties: "suivi-financier",
  showAcquisitionResaleRights: "suivi-financier",
  showAcquisitionSaleFees: "suivi-financier",
  showProduction: "suivi-financier",
  showTransport: "suivi-financier",
  showRestoration: "suivi-financier",
  showFraming: "suivi-financier",
  showPhoto: "suivi-financier",
  showExpertise: "suivi-financier",
  showSuiviFinancierNotes: "suivi-financier",
  // -- Description (onglet "description") --
  showCreationPlace: "description",
  showInventoryNumber: "description",
  showCollectionRef: "description",
  showDoubleFace: "description",
  oeuvresDescriptionSousTitre: "description",
  oeuvresDescriptionAutreTitre: "description",
  oeuvresDescriptionReference: "description",
  showCategory: "description",
  showTheme: "description",
  showSeries: "description",
  showSupport: "description",
  showMovement: "description",
  showGeographicArea: "description",
  showPeriod: "description",
  // -- Notes (onglet "notes") --
  oeuvresValidationRemarquesPubliques: "notes",
  showPersonalNotes: "notes",
  // -- Dimensions & poids (onglet "dimensions-poids") --
  showSurfaceVolume: "dimensions-poids",
  showCustomDimensions: "dimensions-poids",
  showWeight: "dimensions-poids",
  showFrameDimensions: "dimensions-poids",
  // -- Localisation / historique (onglet "localisation-historique") --
  showCurrentCollection: "localisation-historique",
  showNotLocated: "localisation-historique",
  oeuvresLocalisationHistorique: "localisation-historique",
  // -- Rangement (onglet "rangement-recolement") --
  showStorageLocation: "rangement-recolement",
  showStorageRoom: "rangement-recolement",
  showStorageRow: "rangement-recolement",
  showStorageRack: "rangement-recolement",
  showStorageFurniture: "rangement-recolement",
  showStorageShelf: "rangement-recolement",
  showStorageBox: "rangement-recolement",
  showStorageBinder: "rangement-recolement",
  showStorageOtherRef: "rangement-recolement",
  showStorageDate: "rangement-recolement",
  // -- Régie (onglet "regie") --
  oeuvresRegieNotesCondition: "regie",
  showRestored: "regie",
  showToRestore: "regie",
  // -- Photos / crédits (onglet "photo-copyright") --
  showCreditsArtwork: "photo-copyright",
  showCreditsPhoto: "photo-copyright",
  // -- Documents administratifs (onglet "admin-docs") --
  oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs: "admin-docs",
  // -- Description (extended, onglet "description") — les champs de
  //    l'onglet non masquable "generalInformation" (autre n°, ancien n°,
  //    notation, résumé, mots-clés) sont volontairement omis : toujours gardés.
  showFormerTitle: "description",
  showArtworkType: "description",
  showSupportRemark: "description",
  showManufacturing: "description",
  showLongDescription: "description",
  showDigitalFileName: "description",
  // -- Localisation / historique (extended) --
  showCollectionCatalogueName: "localisation-historique",
  showCollectionNote: "localisation-historique",
  showDepositDate: "localisation-historique",
  showDepositReturnDate: "localisation-historique",
  showDepositNote: "localisation-historique",
  // -- Régie (onglet "regie") --
  showFrameDescription: "regie",
  showRegieNotes: "regie",
  showCrateDescription: "regie",
  showCrateDimensions: "regie",
  showCrateWeight: "regie",
  showCrateWeightWithArtwork: "regie",
  // -- Rangement / récolement (onglet "rangement-recolement") --
  showStorageNotes: "rangement-recolement",
  showMarkingDate: "rangement-recolement",
  showRecolementOperator: "rangement-recolement",
  showRecolementDate: "rangement-recolement",
  showRecolementCampaign: "rangement-recolement",
  showRecolementOtherRef: "rangement-recolement",
  showRecolementNotes: "rangement-recolement",
  // -- Crédits / droits (onglet "photo-copyright") --
  showRightsNature: "photo-copyright",
  showRightsAmount: "photo-copyright",
  showRightsRemark: "photo-copyright",
  // -- Sources (onglet "sources") --
  showSources: "sources",
  // -- Relations / enregistrements liés (onglet "links") --
  relatedArtworks: "links",
  oeuvresDansExposition: "links",
  oeuvresDansBibliographie: "links",
  oeuvresDansVentes: "links",
  relatedAudiovisuals: "links",
  relatedDocuments: "links",
  relatedWritings: "links",
  relatedPhotos: "links",
  relatedMultipleprints: "links",
};

/** True si le réglage d'impression appartient à un onglet actuellement masqué. */
export function isPrintSettingTabHidden(
  settingKey: keyof ArtworkPrintSettings,
  hiddenTabIds: readonly string[],
): boolean {
  const tab = PRINT_SETTING_TO_ARTWORK_TAB[settingKey];
  return tab != null && hiddenTabIds.includes(tab);
}

/**
 * Force à `false` tous les réglages dont l'onglet est masqué. Appliqué en plus
 * du masquage champ par champ : un onglet masqué ne doit jamais s'imprimer.
 */
export function hideTabbedFieldsInArtworkPrintSettings(
  settings: ArtworkPrintSettings,
  hiddenTabIds: readonly string[],
): ArtworkPrintSettings {
  if (hiddenTabIds.length === 0) return settings;
  const next = { ...settings };
  for (const settingKey of Object.keys(
    PRINT_SETTING_TO_ARTWORK_TAB,
  ) as (keyof ArtworkPrintSettings)[]) {
    if (isPrintSettingTabHidden(settingKey, hiddenTabIds)) {
      next[settingKey] = false as ArtworkPrintSettings[typeof settingKey];
    }
  }
  return next;
}

/** Whether a mapped grid field is currently shown in the artwork fiche. */
export function isMappedFieldVisible(
  fieldName: string,
  visibility: FormFieldsVisibilityState,
): boolean {
  // Non-maskable fields (title, registration number…) stay visible even after
  // "hide all" — handled centrally by isFieldVisibleInFiche for table "artwork".
  return isFieldVisibleInFiche("artwork", fieldName, visibility);
}

/**
 * Returns a copy of `settings` where the mapped keys are forced to match the
 * grid (visible field → true, hidden field → false). Unmapped settings are
 * left exactly as the user set them.
 */
export function applyVisibilityToArtworkPrintSettings(
  settings: ArtworkPrintSettings,
  visibility: FormFieldsVisibilityState,
): ArtworkPrintSettings {
  const next = { ...settings };
  for (const [settingKey, fieldName] of Object.entries(
    ARTWORK_PRINT_SETTINGS_VISIBILITY_MAP,
  )) {
    next[settingKey as keyof ArtworkPrintSettings] = isMappedFieldVisible(
      fieldName,
      visibility,
    );
  }
  return next;
}
